import React, { FC, KeyboardEvent, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import { Popover, IconButton, InputBase, Tooltip, Divider, Typography, makeStyles } from '@material-ui/core';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LinkIcon from 'components/icons/LinkIcon';
import { isValidUrl } from 'utils/urlUtils';
import ToolbarButton from './ToolbarButton';

interface LinkPopoverProps {
  editor: Editor | null;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  linkPaper: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    width: 400,
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  linkInput: {
    flex: 1,
    minWidth: 0,
    paddingLeft: theme.spacing(1),
  },
  linkError: {
    padding: theme.spacing(0.5, 1),
  },
}));

export const useLinkPopover = (editor: Editor | null, disabled = false) => {
  const locked = disabled || !editor;
  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const linkAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');

  function openLinkPopover() {
    if (!editor || locked) return;

    const href: unknown = editor.getAttributes('link').href;

    setLinkUrl(typeof href === 'string' ? href : '');
    setLinkError('');
    setLinkOpen(true);
  }

  const applyLink = () => {
    if (!editor || locked) return;

    const href = linkUrl.trim();

    if (!isValidUrl(href)) {
      setLinkError('Enter an absolute HTTP or HTTPS URL.');
      return;
    }

    const chain = editor.chain().focus().extendMarkRange('link');

    if (editor.state.selection.empty && !editor.isActive('link')) {
      chain
        .insertContent({
          type: 'text',
          text: href,
          marks: [{ type: 'link', attrs: { href } }],
        })
        .command(({ tr, state }) => {
          tr.removeStoredMark(state.schema.marks.link);
          return true;
        })
        .run();
    } else {
      chain.setLink({ href }).run();
    }

    setLinkOpen(false);
  };

  const removeLink = () => {
    if (!editor || locked) return;

    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  const closeLinkPopover = () => setLinkOpen(false);

  const changeLinkUrl = (value: string) => {
    setLinkUrl(value);
    setLinkError('');
  };

  const openLink = () => {
    const href = linkUrl.trim();
    if (!locked && isValidUrl(href)) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyLink();
    }
    if (event.key === 'Escape') {
      closeLinkPopover();
      editor?.commands.focus();
    }
  };

  const focusLinkInput = () => linkInputRef.current?.focus();

  return {
    linkInputRef,
    linkAnchorRef,
    linkOpen: linkOpen && !locked,
    linkUrl,
    linkError,
    locked,
    active: linkOpen || Boolean(editor?.isActive('link')),
    canApply: !locked && Boolean(linkUrl.trim()),
    canOpen: !locked && isValidUrl(linkUrl.trim()),
    canRemove: !locked && Boolean(editor?.isActive('link')),
    openLinkPopover,
    closeLinkPopover,
    changeLinkUrl,
    applyLink,
    removeLink,
    openLink,
    handleKeyDown,
    focusLinkInput,
  };
};

export const LinkPopover: FC<LinkPopoverProps> = ({ editor, disabled = false }) => {
  const classes = useStyles();
  const {
    linkInputRef,
    linkAnchorRef,
    linkOpen,
    linkUrl,
    linkError,
    locked,
    active,
    canApply,
    canOpen,
    canRemove,
    openLinkPopover,
    closeLinkPopover,
    changeLinkUrl,
    applyLink,
    removeLink,
    openLink,
    handleKeyDown,
    focusLinkInput,
  } = useLinkPopover(editor, disabled);

  return (
    <>
      <span ref={linkAnchorRef}>
        <ToolbarButton label={<LinkIcon />} active={active} disabled={locked} onClick={openLinkPopover} />
      </span>
      <Popover
        open={linkOpen}
        anchorEl={linkAnchorRef.current}
        onClose={closeLinkPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.linkPaper }}
        disableRestoreFocus
        TransitionProps={{ onEntered: focusLinkInput }}
      >
        <div role="dialog" aria-label="Add or edit link">
          <div className={classes.linkRow}>
            <InputBase
              inputRef={linkInputRef}
              className={classes.linkInput}
              placeholder="Paste a link…"
              value={linkUrl}
              disabled={locked}
              inputProps={{
                'aria-label': 'Link URL',
                'aria-invalid': Boolean(linkError),
              }}
              onChange={event => changeLinkUrl(event.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Tooltip title="Apply link">
              <span>
                <IconButton size="small" aria-label="Apply link" disabled={!canApply} onClick={applyLink}>
                  <KeyboardReturnIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
            <Tooltip title="Open link">
              <span>
                <IconButton size="small" aria-label="Open link in a new tab" disabled={!canOpen} onClick={openLink}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remove link">
              <span>
                <IconButton size="small" aria-label="Remove link" disabled={!canRemove} onClick={removeLink}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          {linkError && (
            <Typography className={classes.linkError} color="error" role="alert" variant="caption" component="div">
              {linkError}
            </Typography>
          )}
        </div>
      </Popover>
    </>
  );
};
